import { useState } from 'react';
import {
  Bell, AlertTriangle, Zap,
  ArrowDownLeft, ArrowUpRight, Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/store/auth';
import { Config } from '@/lib/api';
import { useSoftphone } from '@/store/softphone';
import { useConnection } from '@/store/connection';
import { useAgentState, AGENT_STATE_LABEL } from '@/lib/agentState';
import { useNotifications } from '@/store/notifications';
import { useNotificationsFeed } from '@/lib/useNotificationsFeed';
import { useStateTimer } from '@/lib/useStateTimer';
import { useSipHealthQuery } from '@/lib/useSipHealth';
import { useConnectionHealthy } from '@/lib/useConnectionHealthy';
import { TopbarSoftphoneActions } from '@/components/softphone/TopbarSoftphoneActions';
import clsx from 'clsx';
import type { SipHealth } from '@/lib/api';

interface TopbarProps {
  shortcutItems?: unknown; // kept for compat; help moved to /settings
}

/**
 * ArkConnect topbar — referans mock yapısına uygun:
 *
 *  LEFT  : Brand · status pill (state-colored border)
 *  CENTER: ctx-* panels (state'e göre değişir — idle/active/mola)
 *  RIGHT : softphone-actions (active) · return-btn (mola) · queue toggle
 *          (kuyrukta/kuyruk-disi) · utilities · avatar · logout
 */
export function Topbar(_props: TopbarProps) {
  const { user } = useAuth();
  const agentState = useAgentState();
  const sp = useSoftphone();
  const { state, call, queueIn, queueAction } = sp;
  const { data: pbxCfg } = useQuery({ queryKey: ['pbx-config'], queryFn: Config.pbx });

  // Status pill içindeki agent-time timer — refresh sonrası kaldığı yerden
  // devam, bağlantı koptuğunda donduralım (call-center KPI doğruluğu).
  const connectionHealthy = useConnectionHealthy();
  const agentSeconds = useStateTimer(connectionHealthy);
  // 3CX tarafından doğrulanmış registration durumu — local state yalan
  // söyleyebilir (3CX kapanmış ama Janus state OK görünüyor). AppShell'de
  // recovery hook mount edildi; burada sadece cache'den read.
  const sipHealth = useSipHealthQuery();
  const networkOffline = useSoftphone((s) => s.networkOffline);
  const pbxUnreachableStreak = useSoftphone((s) => s.pbxUnreachableStreak);

  // Bildirim badge — okunmamış sayısı (lastSeenId'den büyük id'ler)
  const openNotifDrawer = useNotifications((s) => s.openDrawer);
  const { unread } = useNotificationsFeed();

  const toggleQueue = () => {
    console.log('[topbar] queue toggle click — state=%s queueIn=%s queueAction=%o pbxCfg=%o',
      state, queueIn, queueAction, pbxCfg ? `${pbxCfg.queue_codes.login}/${pbxCfg.queue_codes.logout}` : 'NOT_LOADED');
    if (!pbxCfg) { console.warn('[topbar] pbxCfg not loaded yet'); return; }
    if (state !== 'registered') { console.warn('[topbar] SIP not registered, cannot dial feature code'); return; }
    if (queueAction) { console.warn('[topbar] previous queueAction still in flight'); return; }
    sp.triggerQueueAction(queueIn ? 'logout' : 'login',
      queueIn ? pbxCfg.queue_codes.logout : pbxCfg.queue_codes.login);
  };

  return (
    <header
      className="arkchrome h-14 flex items-center px-4 gap-4 flex-shrink-0 relative z-50"
      role="banner"
    >
      {/* ─── LEFT: Brand + Status pill + (active) caller card ─── */}
      <div className="flex items-center gap-3.5 flex-shrink-0 min-w-0">
        <div className="leading-none tracking-tight">
          <span className="text-[15px] font-bold text-slate-900">Ark</span>
          <span className="text-[15px] font-semibold text-orange-500">Connect</span>
          <span className="text-[15px] font-bold text-orange-500">.</span>
        </div>
        <div className="w-px h-6 bg-slate-300/70" />
        <StatusPill agentState={agentState} sipState={state} call={call} seconds={agentSeconds} sipHealth={sipHealth} networkOffline={networkOffline} pbxUnreachableStreak={pbxUnreachableStreak} />
        {/* Active caller card sits right next to status pill (referansa uygun).
            Çağrı varken queueAction banner'ından bağımsız göster — gelen çağrı
            kuyruk-login animasyonundan önceliklidir. */}
        {agentState === 'active' && (
          <CallerCardInline call={call} />
        )}
      </div>

      {/* ─── CENTER: state-aware context (idle states only) ──── */}
      <div className="flex-1 flex items-center gap-3 min-w-0">
        <CenterContext
          agentState={agentState}
          sipState={state}
          call={call}
          queueAction={queueAction}
        />
      </div>

      {/* ─── RIGHT: actions ───────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Softphone buttons — HER ZAMAN görünür, state'e göre disabled.
            Kuyruk login/logout artık xapi (SIP call değil) — bu yüzden
            queueAction sırasında gizlemiyoruz; aksi halde tam o sırada gelen
            çağrıyı Cevapla butonu gizlenip kullanıcı çağrıyı açamıyordu. */}
        <TopbarSoftphoneActions />

        {/* iOS-style queue toggle — her durumda görünür, sadece kuyrukta veya
            kuyruk-disi'da işe yarar. Çağrı sırasında pasif. */}
        <QueueIosToggle
          queueIn={queueIn}
          disabled={!pbxCfg || queueAction !== null || (agentState !== 'kuyruk-disi' && agentState !== 'kuyrukta')}
          sipState={state}
          onToggle={toggleQueue}
        />

        {/* Quick utilities — Hızlı dialer + Bildirim */}
        <div className="flex items-center gap-1">
          <button
            title="Hızlı arama"
            data-quickdial-trigger
            onClick={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              sp.openQuickDial({
                top: rect.bottom + 8,
                right: Math.max(8, window.innerWidth - rect.right),
              });
            }}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-brand-600 hover:bg-surface/70 hover:shadow-sm transition-all hover:-translate-y-0.5"
          >
            <Zap size={17} strokeWidth={2.25} />
          </button>
          <button
            title="Bildirimler"
            onClick={openNotifDrawer}
            className="relative w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-brand-600 hover:bg-surface/70 hover:shadow-sm transition-all hover:-translate-y-0.5"
          >
            <Bell size={17} strokeWidth={2.25} />
            {/* Badge — okunmamış varsa göster, ikonu örtmeyecek dış köşede */}
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[#08140d] text-[9px] font-bold font-mono flex items-center justify-center shadow-sm ring-2 ring-base leading-none">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
        </div>

        {/* User avatar + EXT — çıkış sidebar'a taşındı */}
        <div className="flex items-center gap-2.5 ml-1 pl-2.5 border-l border-slate-300/60">
          <div className="leading-tight text-right hidden sm:block">
            <div className="text-[12px] font-semibold text-slate-900 truncate max-w-[120px]">{user?.display_name}</div>
            <div className="text-[10px] text-slate-500 font-mono">EXT {user?.extension}</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[#08140d] flex items-center justify-center font-semibold text-[12px] shadow-[0_4px_10px_-2px_rgba(255,79,31,0.45)] ring-2 ring-base">
            {(user?.display_name ?? '?').slice(0, 2).toUpperCase()}
          </div>
        </div>
      </div>
    </header>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */

function StatusPill({ agentState, sipState, call, seconds, sipHealth, networkOffline, pbxUnreachableStreak }: {
  agentState: ReturnType<typeof useAgentState>;
  sipState: string;
  call: ReturnType<typeof useSoftphone.getState>['call'];
  seconds: number;
  sipHealth: SipHealth | undefined;
  networkOffline: boolean;
  pbxUnreachableStreak: number;
}) {
  // Synthesize "effectively offline" — navigator.onLine lies when VPN
  // dropped but Wi-Fi is still up. xapi unreachable 2+ times in a row is
  // a much more reliable signal of actual broken connectivity.
  // connStatus==='offline' (WS heartbeat) en hızlı + otoriter sinyal — backend
  // down'da pbxUnreachableStreak artık gate'li (büyümez), o yüzden bu şart.
  const connStatus = useConnection((s) => s.status);
  const effectivelyOffline = connStatus === 'offline' || networkOffline || pbxUnreachableStreak >= 2;
  // SIP henüz register olmadıysa "kuyruk dışı" demek yanıltıcı — bunun yerine
  // gerçek SIP durumunu göster (Bağlanıyor / Bağlantı yok).
  const isConnecting = sipState === 'idle' || sipState === 'registering';
  const isOffline = sipState === 'register_failed';
  // 3CX tarafından "kayıtlı değilsin" demesi ama bizim state'imiz registered —
  // Janus'un re-register denediği geçiş anı. Kullanıcıya "Hazır" gösterip
  // çağrı kaçırmaktansa sarı uyarı göster.
  const isStale = !!sipHealth && sipHealth.pbx_reachable && !sipHealth.registered
    && sipState === 'registered';
  // 3CX'e ulaşamıyoruz — registered durumumuz şüpheli ama Janus state OK
  // diyorsa muhtemelen network blip, kullanıcıyı paniğe sürüklemeyelim.
  const isPbxUnreachable = !!sipHealth && !sipHealth.pbx_reachable;

  let label: string;
  let ring: string;
  let dot: string;
  let tooltip: string | undefined;
  if (effectivelyOffline) {
    // Highest priority — overrides everything. Includes both:
    //  - navigator.onLine === false (Wi-Fi/Ethernet down)
    //  - pbxUnreachableStreak >= 2 (VPN dropped silently, or PBX down)
    label = 'Bağlantı Yok';
    ring = 'border-red-500 text-red-700';
    dot = 'bg-red-500 animate-pulse';
    tooltip = networkOffline
      ? 'İnternet bağlantısı yok — Wi-Fi/VPN kontrol et'
      : 'Sunucuya ulaşılamıyor — VPN/ağ bağlantısı kontrol et';
  } else if (isOffline) {
    label = 'Bağlantı Yok';
    ring = 'border-red-400 text-red-700';
    dot = 'bg-red-500';
    tooltip = 'SIP register başarısız — sunucu/credential kontrolü gerek';
  } else if (isConnecting) {
    label = 'Bağlanıyor…';
    ring = 'border-amber-400 text-amber-700';
    dot = 'bg-amber-500 animate-pulse';
    tooltip = 'SIP kaydı yapılıyor, lütfen bekle…';
  } else if (isStale) {
    // 3CX agent'i kayıtlı görmüyor → useSipHealth force-reregister tetikledi,
    // birkaç saniye içinde düzelir. UI sarı.
    label = 'Yeniden bağlanıyor…';
    ring = 'border-amber-400 text-amber-700';
    dot = 'bg-amber-500 animate-pulse';
    tooltip = '3CX kaydını yeniliyor — bu uyarıyı görüyorsan çağrı kabul etme';
  } else if (isPbxUnreachable && sipState === 'registered') {
    // PBX'e ulaşılamıyor — "Hazır" yalanı UI'da göstermek hatalı; agent
    // çağrı bekleyebilir ama gerçek registration belirsiz. Label "Kontrol
    // ediliyor" + sarı border + sarı dot. useSipHealthRecovery 3 streak'te
    // forceRegister tetikler; bu arada timer da durur (showTimer=false).
    label = 'Kontrol ediliyor…';
    ring = 'border-amber-400 text-amber-700';
    dot = 'bg-amber-500 animate-pulse';
    tooltip = '3CX xapi cevap vermiyor (502/timeout) — registration durumu belirsiz, kontrol ediliyor';
  } else if (agentState === 'active') {
    // SIP state'e göre daha doğru etiket
    if (sipState === 'in_call') label = 'Görüşmede';
    else if (sipState === 'ringing' && call?.direction === 'inbound') label = 'Çalıyor';
    else if (sipState === 'ringing' && call?.direction === 'outbound') label = 'Çağrılıyor';
    else if (sipState === 'calling') label = 'Aranıyor';
    else label = AGENT_STATE_LABEL.active;
    ring = 'border-orange-500 text-orange-500';
    dot = 'bg-orange-500 animate-pulse';
  } else {
    label = AGENT_STATE_LABEL[agentState];
    ring = {
      'kuyruk-disi': 'border-slate-300 text-slate-700',
      'kuyrukta':    'border-emerald-400 text-emerald-700',
      'active':      'border-brand-400 text-brand-700',
      'mola':        'border-amber-400 text-amber-700',
    }[agentState];
    dot = {
      'kuyruk-disi': 'bg-slate-400',
      'kuyrukta':    'bg-emerald-500 animate-pulse',
      'active':      'bg-brand-500 animate-pulse',
      'mola':        'bg-amber-500 animate-pulse',
    }[agentState];
  }

  // Sayaç SADECE bir şey aktif sayılırken anlamlı: Görüşmede (çağrı süresi —
  // başka hiçbir yerde yok) ve Mola (seans süresi, limit takibi). Kuyrukta/
  // kuyruk-dışı'nda gizli — "Kuyrukta" sayacı zaten Workspace'teki "Boşta"
  // kartıyla birebir aynı (net tekrar). Pill boşken sade kalır.
  const timerMeaningful = agentState === 'active' || agentState === 'mola';
  // Timer sadece bağlantı SAĞLAM iken sayar. Connecting/offline/stale/PBX-
  // unreachable/network-offline durumlarda dondurmak doğru — "online
  // geçirilen süre" yalanı olmasın. Bağlantı geri gelince timer kaldığı
  // yerden devam (useStateTimer freeze logic).
  const showTimer = timerMeaningful && !effectivelyOffline && !isConnecting && !isOffline && !isStale && !isPbxUnreachable;
  return (
    <button
      className={clsx(
        'inline-flex items-center gap-2 px-3 py-1.5 bg-surface rounded-full text-[12px] font-medium border-[1.5px] shadow-sm hover:-translate-y-px hover:shadow-md transition',
        ring,
      )}
      title={tooltip}
    >
      <span className={clsx('w-1.5 h-1.5 rounded-full', dot)} />
      <span>{label}</span>
      {showTimer && (
        <span className="font-mono tabular-nums opacity-75 ml-0.5">{fmtHMS(seconds)}</span>
      )}
    </button>
  );
}

function fmtHMS(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function CenterContext({ agentState, sipState, call, queueAction }: {
  agentState: ReturnType<typeof useAgentState>;
  sipState: string;
  call: ReturnType<typeof useSoftphone.getState>['call'];
  queueAction: ReturnType<typeof useSoftphone.getState>['queueAction'];
}) {
  // Aktif/çalan çağrı queue-login banner'ından önceliklidir — çağrı sırasında
  // caller bilgisi gösterilir (CallerCardInline), banner gizlenir.
  const hasLiveCall = sipState === 'ringing' || sipState === 'in_call' || sipState === 'calling';
  // Kuyruğa giriş/çıkış xapi onayı — kısa banner (çağrı yokken).
  if (queueAction && !hasLiveCall) {
    const isLogin = queueAction.kind === 'login';
    const isDone = queueAction.status === 'done';
    return (
      <div className="flex items-center gap-2.5 text-[12.5px]">
        <span className="font-medium text-emerald-700">
          {isDone
            ? (isLogin ? 'Kuyruğa katıldın' : 'Kuyruktan çıktın')
            : (isLogin ? 'Kuyruğa katılıyor…' : 'Kuyruktan çıkılıyor…')}
        </span>
      </div>
    );
  }
  // SIP register edilirken queue toggle çalışmaz → kullanıcıya doğru mesaj ver
  if (sipState === 'idle' || sipState === 'registering') {
    return (
      <div className="flex items-center gap-3 text-[12.5px]">
        <span className="font-medium text-amber-800">SIP kaydı yapılıyor…</span>
        <span className="w-px h-3.5 bg-slate-300" />
        <span className="text-slate-500">Tamamlanınca kuyruğa girebilirsin</span>
      </div>
    );
  }
  if (sipState === 'register_failed') {
    return (
      <div className="flex items-center gap-3 text-[12.5px]">
        <span className="font-medium text-red-700">SIP register başarısız</span>
        <span className="w-px h-3.5 bg-slate-300" />
        <span className="text-slate-500">Ses ayarlarını ve sunucuyu kontrol et</span>
      </div>
    );
  }
  if (agentState === 'kuyruk-disi') {
    return (
      <div className="flex items-center gap-3 text-[12.5px] text-slate-700">
        <span className="font-medium">Kuyruk dışındasın</span>
        <span className="w-px h-3.5 bg-slate-300" />
        <span className="text-slate-500">Çağrı alabilmek için kuyruğa gir</span>
      </div>
    );
  }
  if (agentState === 'kuyrukta') {
    return (
      <div className="flex items-center gap-3 text-[12.5px] text-slate-700">
        <span className="font-medium">Hat hazır</span>
        <span className="w-px h-3.5 bg-slate-300" />
        <span className="text-slate-500 font-mono tabular-nums">Çağrı bekleniyor</span>
      </div>
    );
  }
  if (agentState === 'mola') {
    // SOLID amber rozet + koyu metin — koyu temada en yüksek kontrast/okunurluk.
    // (translucent amber bg + açık amber metin "amber-on-amber" olup okunmuyordu;
    //  eski açık-tema krem kutu da "sönük"tü.)
    return (
      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-400 ring-1 ring-amber-300/60 rounded-lg text-amber-950 text-[12.5px] shadow-sm">
        <AlertTriangle size={14} className="text-amber-900" />
        <strong className="font-bold">Şu an moladasın</strong>
        <span className="opacity-40 tracking-wider">· · ·</span>
        <span className="font-medium">Döndüğünde kuyruğa girmelisin</span>
      </div>
    );
  }
  // active — caller card moved next to status pill via CallerCardInline
  return null;
}

/**
 * 3CX kuyruk çağrılarında displayName = "QUEUE:NUMBER" formatında gelir
 * (örn. "DAGITIM:05326108621"). Kuyruk adını, arayan numarayı ve (kuyruk
 * değilse) kişi adını ayır — temsilci hangi kuyruktan geldiğini + numarayı görsün.
 */
function parseCaller(call: { displayName?: string; peer: string }): {
  queue: string | null;
  caller: string;
  contactName: string | null;
} {
  const peer = call.peer ?? '';
  const raw = (call.displayName ?? '').replace(/^"+|"+$/g, '').trim();
  if (!raw) return { queue: null, caller: peer, contactName: null };
  const idx = raw.indexOf(':');
  if (idx > 0) {
    const left = raw.slice(0, idx).trim();
    const right = raw.slice(idx + 1).trim();
    // Sağ taraf numara (peer'a eşit ya da rakam dizisi) → kuyruk çağrısı
    if (right && (right === peer || /^\+?\d{3,}$/.test(right))) {
      return { queue: left, caller: right || peer, contactName: null };
    }
  }
  // Kuyruk değil — displayName ya kişi adı ya da numaranın kendisi
  const isNumber = raw === peer || /^\+?\d{3,}$/.test(raw);
  return { queue: null, caller: peer, contactName: isNumber ? null : raw };
}

/**
 * Topbar caller card — status pill'in sağına yapışır. Hem ringing hem in_call
 * sırasında gösterilir; ringing'de süre yerine "Çalıyor" gösterir.
 */
function CallerCardInline({ call }: { call: ReturnType<typeof useSoftphone.getState>['call'] }) {
  if (!call) return null;
  const { queue, caller, contactName } = parseCaller(call);
  const mainLabel = contactName ?? caller;
  const avatarInitial = contactName ? contactName.slice(0, 1).toUpperCase() : null;
  return (
    <div className="inline-flex items-center gap-2.5 bg-brand-50/90 ring-1 ring-brand-200 rounded-lg pl-1 pr-3 py-1 max-w-[480px] min-w-0">
      <div className="w-8 h-8 rounded-full bg-brand-500 text-[#08140d] flex items-center justify-center flex-shrink-0">
        {queue ? <Users size={15} /> : <span className="text-[11px] font-semibold">{avatarInitial ?? '#'}</span>}
      </div>
      <div className="leading-tight min-w-0">
        <div className="text-[13px] font-semibold text-slate-900 truncate font-mono tabular-nums">
          {mainLabel}
        </div>
        <div className="text-[11px] truncate flex items-center gap-1.5 mt-0.5 min-w-0">
          {queue && (
            <span className="inline-flex items-center gap-1 px-1.5 py-px rounded bg-brand-100 text-brand-700 font-medium text-[10px] flex-shrink-0">
              <Users size={9} /> {queue}
            </span>
          )}
          {contactName && <span className="text-slate-500 font-mono truncate">{caller}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 pl-2.5 ml-0.5 border-l border-brand-200 text-brand-700 text-[11px] font-medium flex-shrink-0">
        {call.direction === 'inbound'
          ? <ArrowDownLeft size={11} />
          : <ArrowUpRight size={11} />}
        <span>{call.direction === 'inbound' ? 'Gelen' : 'Giden'}</span>
      </div>
    </div>
  );
}

function QueueIosToggle({ queueIn, disabled, sipState, onToggle }: {
  queueIn: boolean;
  disabled: boolean;
  sipState: string;
  onToggle: () => void;
}) {
  const sipReady = sipState === 'registered';
  const title = !sipReady
    ? `SIP henüz hazır değil (durum: ${sipState}) — kuyruk komutu gönderilemez`
    : queueIn ? 'Kuyruktan çık' : 'Kuyruğa katıl';
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      title={title}
      className={clsx(
        'inline-flex items-center gap-2 pl-3 pr-1.5 py-1 bg-surface rounded-full text-[12px] font-medium border-[1.5px] shadow-sm hover:-translate-y-px hover:shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0',
        queueIn ? 'border-emerald-400 text-emerald-700' : 'border-slate-300 text-slate-700',
      )}
    >
      <span>{queueIn ? 'Kuyrukta' : 'Kuyruk dışı'}</span>
      <span className={clsx(
        'relative w-8 h-[18px] rounded-full transition-colors',
        queueIn ? 'bg-emerald-500' : 'bg-slate-400',
      )}>
        <span className={clsx(
          'absolute top-0.5 w-3.5 h-3.5 bg-surface rounded-full shadow transition-[left]',
          queueIn ? 'left-[16px]' : 'left-0.5',
        )} />
      </span>
    </button>
  );
}

